/**
 * MedicationPlanPage
 *
 * Dedicated page for managing medication reminders.
 * Shows active and paused reminders with pause/resume/edit/delete actions.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Pill,
  Loader2,
  Pause,
  Play,
  Pencil,
  Trash2,
  Clock,
  Calendar,
  AlertTriangle,
  Baby,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { isValidUUID } from '@/lib/utils';
import { getBabiesFromPregnancy } from '@/lib/babyUtils';
import { useBabyCare } from '../hooks/useBabyCare';
import { useMedicationReminders } from '../hooks/useMedicationReminders';
import { MedicationReminderModal } from '../components/MedicationReminderModal';
import type {
  MedicationReminder,
  CreateMedicationReminder,
  UpdateMedicationReminder,
} from '@/services/medicationReminderService';

/**
 * Format the next reminder time relative to now
 */
function formatNextReminder(nextAt: string | null, t: (key: string) => string): string {
  if (!nextAt) return t('medicationPlan.noNextReminder');

  const next = new Date(nextAt);
  const now = new Date();
  const diffMs = next.getTime() - now.getTime();

  if (diffMs < 0) return t('medicationPlan.overdue');

  const diffMinutes = Math.floor(diffMs / 1000 / 60);
  if (diffMinutes < 60) {
    return t('medicationPlan.inMinutes').replace('{{minutes}}', String(diffMinutes));
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    const time = `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
    return t('medicationPlan.todayAt').replace('{{time}}', time);
  }

  // Tomorrow or later
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (next.toDateString() === tomorrow.toDateString()) {
    const time = `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
    return t('medicationPlan.tomorrowAt').replace('{{time}}', time);
  }

  const date = `${String(next.getDate()).padStart(2, '0')}.${String(next.getMonth() + 1).padStart(2, '0')}.`;
  const time = `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
  return `${date} ${time}`;
}

/**
 * Format the schedule description
 */
function formatSchedule(reminder: MedicationReminder, t: (key: string) => string): string {
  if (reminder.schedule_mode === 'interval') {
    return t('medicationPlan.scheduleInterval').replace('{{hours}}', String(reminder.interval_hours));
  }

  let times: string[] = [];
  const raw = reminder.fixed_times;
  if (Array.isArray(raw)) {
    times = raw;
  } else if (typeof raw === 'string') {
    try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) times = parsed; } catch { /* ignore */ }
  }
  if (times.length === 0) return '';
  return t('medicationPlan.scheduleFixedTimes').replace(
    '{{times}}',
    times.sort().join(', ')
  );
}

export function MedicationPlanPage() {
  const { t } = useTranslation('babyCare');
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const isBabyCareEnabled = useFeatureEnabled('baby_care_enabled');
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);

  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  const wasBirth = lastCompletedPregnancy?.completion_type === 'birth';
  const isBabyMode = appMode === 'baby' && wasBirth;

  const {
    activeReminders,
    pausedReminders,
    isLoading,
    addReminder,
    editReminder,
    togglePause,
    removeReminder,
  } = useMedicationReminders();

  const { entries } = useBabyCare();

  // Build a map of medication_name -> last taken date from baby_care_entries
  const lastTakenMap: Record<string, Date> = {};
  entries.forEach(e => {
    if (e.care_type === 'medication' && e.medication_name) {
      const key = e.medication_name.toLowerCase().trim();
      const date = new Date(e.recorded_at);
      if (!lastTakenMap[key] || date > lastTakenMap[key]) {
        lastTakenMap[key] = date;
      }
    }
  });

  // Periodic tick to update relative time displays (e.g. "Heute um 11:30" → "Überfällig")
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<MedicationReminder | undefined>();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Feature disabled
  if (!isBabyCareEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Not in baby mode
  if (!isBabyMode || !pregnancyId) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Baby className="w-8 h-8 text-white/40" />
        </div>
        <h2 className="text-white font-medium text-lg mb-2">{t('pageTitle')}</h2>
        <p className="text-white/60 text-sm max-w-xs mx-auto">
          {t('notAvailable')}
        </p>
      </div>
    );
  }

  const handleSave = async (
    data: CreateMedicationReminder | { id: string; updates: UpdateMedicationReminder }
  ): Promise<boolean> => {
    if ('id' in data) {
      const result = await editReminder(data.id, data.updates);
      return !!result;
    }
    const result = await addReminder(data);
    return !!result;
  };

  const handleEdit = (reminder: MedicationReminder) => {
    setEditingReminder(reminder);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    await removeReminder(id);
    setConfirmDeleteId(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReminder(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-white text-xl font-semibold">
          {t('medicationPlan.title')}
        </h1>
        <button
          onClick={() => {
            setEditingReminder(undefined);
            setShowModal(true);
          }}
          className="w-10 h-10 rounded-[10px] bg-pp-orange flex items-center justify-center text-white hover:bg-pp-orange/80 transition-colors"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Active Reminders */}
      {activeReminders.length > 0 && (
        <section className="mb-6">
          <div className="space-y-3">
            {activeReminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                babyName={babies[reminder.baby_index]?.name || 'Baby'}
                lastTaken={lastTakenMap[reminder.medication_name.toLowerCase().trim()] ?? null}
                t={t}
                onEdit={() => handleEdit(reminder)}
                onPause={() => togglePause(reminder.id, true)}
                onDelete={() => setConfirmDeleteId(reminder.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Paused Reminders */}
      {pausedReminders.length > 0 && (
        <section className="mb-6">
          <h2 className="text-white/50 text-sm font-medium mb-3">
            {t('medicationPlan.pausedSection')}
          </h2>
          <div className="space-y-3">
            {pausedReminders.map((reminder) => (
              <div
                key={reminder.id}
                className="rounded-[15px] glass-effect p-4 opacity-60"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <Pill className="w-5 h-5 text-white/40" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{reminder.medication_name}</p>
                      {reminder.medication_dose && (
                        <p className="text-white/50 text-sm">{reminder.medication_dose}</p>
                      )}
                      <p className="text-white/40 text-xs mt-1">
                        {t('medicationPlan.paused')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => togglePause(reminder.id, false)}
                      className="w-9 h-9 rounded-[8px] bg-white/10 flex items-center justify-center text-green-400 hover:bg-green-500/20"
                      title={t('medicationPlan.resume')}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(reminder.id)}
                      className="w-9 h-9 rounded-[8px] bg-white/10 flex items-center justify-center text-white/50 hover:text-red-400 hover:bg-red-500/10"
                      title={t('medicationPlan.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {activeReminders.length === 0 && pausedReminders.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
            <Pill className="w-8 h-8 text-white/30" />
          </div>
          <h2 className="text-white font-medium text-lg mb-2">
            {t('medicationPlan.emptyTitle')}
          </h2>
          <p className="text-white/60 text-sm max-w-xs mx-auto mb-6">
            {t('medicationPlan.emptyDescription')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-3 bg-pp-orange text-white font-semibold rounded-[15px] hover:bg-pp-orange/80 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('medicationPlan.addFirst')}
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <MedicationReminderModal
          pregnancyId={pregnancyId}
          existingReminder={editingReminder}
          onSave={handleSave}
          onClose={handleCloseModal}
        />
      )}

      {/* Delete Confirmation Portal */}
      {confirmDeleteId &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/60 z-[9998]"
              onClick={() => setConfirmDeleteId(null)}
            />
            <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[9999] rounded-[20px] glass-effect p-6 max-w-sm mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-white font-semibold text-lg">
                  {t('medicationPlan.deleteConfirmTitle')}
                </h3>
              </div>
              <p className="text-white/70 text-sm mb-6">
                {t('medicationPlan.deleteConfirmMessage')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="flex-1 py-3 rounded-[10px] bg-white/10 text-white font-medium"
                >
                  {t('medicationPlan.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(confirmDeleteId)}
                  className="flex-1 py-3 rounded-[10px] bg-red-500 text-white font-medium"
                >
                  {t('medicationPlan.delete')}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// ── Reminder Card ──────────────────────────────────────────────

interface ReminderCardProps {
  reminder: MedicationReminder;
  babyName: string;
  lastTaken: Date | null;
  t: (key: string) => string;
  onEdit: () => void;
  onPause: () => void;
  onDelete: () => void;
}

function ReminderCard({ reminder, babyName, lastTaken, t, onEdit, onPause, onDelete }: ReminderCardProps) {
  const schedule = formatSchedule(reminder, t);
  const nextReminder = formatNextReminder(reminder.next_reminder_at, t);

  return (
    <div className="rounded-[15px] glass-effect p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-pp-orange/20 flex items-center justify-center">
            <Pill className="w-5 h-5 text-pp-orange" />
          </div>
          <div>
            <p className="text-white font-medium">{reminder.medication_name}</p>
            {reminder.medication_dose && (
              <p className="text-white/60 text-sm">{reminder.medication_dose}</p>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="w-9 h-9 rounded-[8px] bg-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/20"
            title={t('medicationPlan.edit')}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onPause}
            className="w-9 h-9 rounded-[8px] bg-white/10 flex items-center justify-center text-white/50 hover:text-yellow-400 hover:bg-yellow-500/10"
            title={t('medicationPlan.pause')}
          >
            <Pause className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="w-9 h-9 rounded-[8px] bg-white/10 flex items-center justify-center text-white/50 hover:text-red-400 hover:bg-red-500/10"
            title={t('medicationPlan.delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Schedule info */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span className="text-white/50 flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {schedule}
        </span>
        {reminder.end_date && (
          <span className="text-white/50 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {t('medicationPlan.until')} {new Date(reminder.end_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
          </span>
        )}
      </div>

      {/* Next reminder */}
      <div className="mt-2 px-3 py-1.5 rounded-[8px] bg-white/5 inline-block">
        <span className="text-pp-orange text-sm font-medium">
          {t('medicationPlan.next')}: {nextReminder}
        </span>
      </div>

      {/* Baby name for multi-child */}
      {babyName !== 'Baby' && (
        <p className="text-white/40 text-xs mt-2">{babyName}</p>
      )}

      {/* Notes */}
      {reminder.notes && (
        <p className="text-white/40 text-xs mt-1 italic">{reminder.notes}</p>
      )}

      {/* Last taken */}
      {lastTaken && (
        <p className="text-white/50 text-xs mt-2">
          Zuletzt: {lastTaken.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
          {lastTaken.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
}
